import type { TextBlockConfig } from "@/lib/types/flow";

/**
 * The markdown a text block renders and edits. QL-296: the block is one
 * "Content" field whose heading lives inside the markdown. A short-lived first
 * pass stored the heading in a separate `title`; fold it in as the same heading
 * so nothing authored then is lost, and the next save drops the field.
 */
export function textBlockContent(config: Pick<TextBlockConfig, "title" | "content">): string {
  const content = config.content || "";
  const title = config.title?.trim();
  if (!title) return content;
  return content ? `### ${title}\n\n${content}` : `### ${title}`;
}

const HEADING_LINE = /^\s{0,3}#{1,6}\s+(.+?)(?:\s+#+)?\s*$/;
const FENCE_LINE = /^\s{0,3}(`{3,}|~{3,})/;

/**
 * Inline markdown reduced to its text, for a label: links keep their text,
 * emphasis / code / strikethrough markers go, and backslash escapes resolve.
 */
function plainInlineText(markdown: string): string {
  let text = markdown
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/`([^`]*)`/g, "$1")
    .replace(/~~(.+?)~~/g, "$1");
  // Emphasis nests (***x***, **_x_**), so peel until nothing changes. An
  // underscore inside a word (snake_case) is not emphasis, as in CommonMark.
  for (let previous = ""; previous !== text; ) {
    previous = text;
    text = text
      .replace(/(\*{1,3})(\S(?:.*?\S)?)\1/g, "$2")
      .replace(/(?<!\w)(_{1,3})(\S(?:.*?\S)?)\1(?!\w)/g, "$2");
  }
  return text.replace(/\\([\\`*_{}[\]()#+\-.!~>|])/g, "$1");
}

/**
 * The first heading in a text block, as plain text, for the page editor's row
 * label — a text block has no title of its own, so its heading is what tells
 * two blocks on one page apart. Heading lines inside a fenced code block don't
 * count. Undefined when the block has none.
 */
export function textBlockHeading(
  config: Pick<TextBlockConfig, "title" | "content">,
): string | undefined {
  let inFence = false;
  for (const line of textBlockContent(config).split("\n")) {
    if (FENCE_LINE.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    const match = line.match(HEADING_LINE);
    if (match) return plainInlineText(match[1]).trim() || undefined;
  }
  return undefined;
}
